import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, rectSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import styles from './workspace-filter.module.scss'
import {
  useDeleteFilter,
  useLoadFilterTemplates,
  useSaveFilter,
  useUpdateMilestoneSequence,
} from '../../../../../entities/workflow/hooks/workflow.hooks'
import { useWorkspaceStore } from '../../../../../entities/workflow/stores/workspace.store'
import { MilestoneFilterTemplateDto, MilestoneItem } from '../../../../../entities/workflow/types/workflow.types'
import { WorkspaceFilterControls } from '../workspace-filter-controls/workspace-filter-controls'
import { WorkspaceFilterItem } from '../workspace-filter-item/workspace-filter-item'

type WorkspaceFilterProps = {
  milestones: MilestoneItem[]
}

export const WorkspaceFilter = ({ milestones }: WorkspaceFilterProps) => {
  const { checkedMilestones, setSelectedMilestones } = useWorkspaceStore()

  const [milestonesState, setMilestonesState] = useState<MilestoneItem[]>([])
  const { mutateAsync: updateMilestoneSequence } = useUpdateMilestoneSequence()
  const { mutateAsync: saveFilter } = useSaveFilter()
  const { mutateAsync: deleteFilter } = useDeleteFilter()
  const { data: milestoneFilterTemplates } = useLoadFilterTemplates()
  const sensors = useSensors(useSensor(PointerSensor))

  useEffect(() => {
    setMilestonesState(milestones)
  }, [milestones])

  const handleSelectAll = () => {
    const allIds = milestonesState?.map((milestone) => milestone.id)
    setSelectedMilestones(new Set(allIds))
  }

  const handleRemoveSelection = () => {
    setSelectedMilestones(new Set())
  }

  const handleSearch = (search: string) => {
    if (search) {
      setMilestonesState(milestones?.filter((milestone) => milestone.name.toLowerCase().includes(search.toLowerCase())) ?? [])
    } else {
      setMilestonesState(milestones ?? [])
    }
  }

  const handleCheck = (id: number) => {
    checkedMilestones.has(id) ? checkedMilestones.delete(id) : checkedMilestones.add(id)
    setSelectedMilestones(new Set(checkedMilestones))
  }

  const handleSaveFilter = async (templateName: string) => {
    const data: Omit<MilestoneFilterTemplateDto, 'id'> = {
      name: templateName,
      checkedMilestones: Array.from(checkedMilestones),
    }

    try {
      await saveFilter(data)
      toast.success('Шаблон фильтра сохранен')
    } catch {
      toast.error('Не удалось сохранить шаблон фильтра')
    }
  }

  const handleDeleteFilter = async (id: number) => {
    try {
      await deleteFilter(id)
      toast.success('Шаблон фильтра удален')
    } catch {
      toast.error('Не удалось удалить шаблон фильтра')
    }
  }

  const handleLoadFilter = async (filter: MilestoneFilterTemplateDto) => {
    try {
      await updateMilestoneSequence(
        milestones.map((milestone) => ({
          ...milestone,
          seq: filter.milestoneSeq?.find((seq) => seq.milestone_id === milestone.id)?.seq ?? 0,
        })),
      )
      setSelectedMilestones(new Set(filter.selectedMilestones))
      toast.success('Шаблон фильтра применен')
    } catch {
      toast.error('Не удалось применить шаблон фильтра')
    }
  }

  const handleDragEnd = (event: any) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      setMilestonesState((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id)
        const newIndex = items.findIndex((i) => i.id === over.id)

        const newOrder = arrayMove(items, oldIndex, newIndex)

        const updatedMilestones = newOrder.map((item, index) => ({
          ...item,
          seq: index,
        }))

        updateMilestoneSequence(updatedMilestones)

        return updatedMilestones
      })
    }
  }

  return (
    <>
      <WorkspaceFilterControls
        milestoneFilterTemplates={milestoneFilterTemplates}
        onCheckAll={handleSelectAll}
        onDeleteFilter={handleDeleteFilter}
        onLoadFilter={handleLoadFilter}
        onRemoveSelection={handleRemoveSelection}
        onSaveFilter={handleSaveFilter}
        onSearch={handleSearch}
      />
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <SortableContext
          items={milestonesState.map((m) => m.id)}
          strategy={rectSortingStrategy}
        >
          <div className={clsx('mt-5 mb-5', styles.filterGrid)}>
            {milestonesState?.map((milestone) => (
              <WorkspaceFilterItem
                key={milestone.id}
                isChecked={checkedMilestones.has(milestone.id)}
                item={milestone}
                onToggle={handleCheck}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </>
  )
}
